"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import DocsPageFrame from "../../../components/DocsPageFrame";
import Callout from "../../../components/Callout";
import Screenshot from "../../../components/Screenshot";

const B = ({ children }) => (
  <span className="geist-medium text-[#111827]">{children}</span>
);

const RULES = [
  {
    topic: "Admin only",
    rule: "Settings → Preferences and advanced → Credit limits. Non-admins see nothing",
  },
  {
    topic: "Two independent blocks",
    rule: "Sales Credit Limit Configuration and Purchase Credit Limit Configuration each have their own Save Configuration. Saving one writes that half of creditLimitConfig",
  },
  {
    topic: "Branch vs company",
    rule: "Branch Wise Credit Limit on: per-branch Credit Limit + Restriction Document (and a Total of branch limits). Off: one company Credit Limit + Restriction Document",
  },
  {
    topic: "Payment terms",
    rule: "Branch Wise Payment Terms on: per-branch Payment Terms + Restriction Payment Terms Document. Off: one company Payment Terms + Restriction Payment Terms Document",
  },
];

const sections = [
  {
    heading: "What this is for",
    subsections: [
      { heading: "1. Sales defaults" },
      { heading: "2. Purchase defaults" },
    ],
  },
  { heading: "Business rules" },
];

const Definitions = ({ items }) => (
  <div className="space-y-2.5">
    {items.map((item) => (
      <p
        key={item.term}
        className="text-[16px] geist-regular text-[#374151] leading-relaxed"
      >
        <span className="geist-medium text-[#111827]">{item.term}:</span>{" "}
        {item.desc}
      </p>
    ))}
  </div>
);

const Flow = ({ steps }) => (
  <p className="text-[15px] geist-regular text-[#374151] leading-relaxed mb-4 px-4 py-3 bg-[#FAFAFA] border border-[#E5E5E5] rounded-[10px]">
    {steps.map((step, i) => (
      <span key={`${step}-${i}`}>
        {i > 0 && (
          <span className="text-[#9CA3AF] geist-medium"> {"→"} </span>
        )}
        <span className="geist-medium text-[#111827]">{step}</span>
      </span>
    ))}
  </p>
);

export default function CreditLimitsPage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            Credit Limits
          </h1>
          <h2 className="text-[24px] sentient-medium text-[#111827] mb-3">
            For: Admins — company (or per-branch) default credit limit and
            which documents enforce it
          </h2>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#374151] geist-regular max-w-2xl">
            Customer and vendor records can override these defaults. This
            screen is the company starting point, including payment-terms
            restriction.
          </p>
          <Flow
            steps={[
              "Settings",
              "Credit limits",
              "Sales and/or Purchase",
              "Save Configuration",
            ]}
          />
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[24px] sentient-medium text-[#111827] mb-6 scroll-mt-24"
          >
            What this is for
          </h2>

          <h3
            id="section-0-sub-0"
            className="text-[18px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            1. Sales defaults
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Title <B>Sales Credit Limit Configuration</B>. Restriction
            documents: Quote, Sales Order, Delivery Challan, Invoice.
          </p>
          <Definitions
            items={[
              {
                term: "What it affects",
                desc: "Default sales credit limit and which sales documents are blocked when the customer is over limit or overdue — until a customer overrides it",
              },
            ]}
          />

          <h3
            id="section-0-sub-1"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            2. Purchase defaults
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Title <B>Purchase Credit Limit Configuration</B>. Restriction
            documents: Purchase Order, GRPO, Bill.
          </p>
          <Definitions
            items={[
              {
                term: "Branch table",
                desc: "When either Branch Wise box is on: Active, Division, Branch, Code, then Credit Limit / Restriction Document and/or Payment Terms / Restriction Payment Terms Document. Division, Branch, Code are read-only",
              },
              {
                term: "What it affects",
                desc: "Default purchase credit limit and which purchase documents are blocked when the vendor is over limit — until a vendor overrides it. Purchase also saves the Active flag per branch",
              },
            ]}
          />
          <Callout type="note">
            Customer and Vendor masters can override these company defaults
            per party.
          </Callout>
          <Screenshot caption="Sales and Purchase Credit Limit Configuration — Branch Wise checkboxes, restriction documents, Save Configuration" />
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Business rules
          </h2>
          <div className="border border-[#E5E5E5] rounded-[10px] overflow-hidden">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                  <th className="px-4 py-2.5 text-[13px] geist-medium text-[#111827] w-[28%]">
                    Topic
                  </th>
                  <th className="px-4 py-2.5 text-[13px] geist-medium text-[#111827]">
                    Rule
                  </th>
                </tr>
              </thead>
              <tbody>
                {RULES.map((row) => (
                  <tr
                    key={row.topic}
                    className="border-b border-[#E5E5E5] last:border-b-0"
                  >
                    <td className="px-4 py-2.5 text-[14px] geist-medium text-[#111827] align-top">
                      {row.topic}
                    </td>
                    <td className="px-4 py-2.5 text-[14px] geist-regular text-[#374151]">
                      {row.rule}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        <motion.div
          variants={fadeUp}
          className="flex flex-col sm:flex-row gap-4"
        >
          <Link
            href="/documentation/books/sales/customer-master"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Customer Master <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Per-customer override of these sales defaults
            </p>
          </Link>
          <Link
            href="/documentation/books/purchase/vendors"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Vendors <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Per-vendor override of these purchase defaults
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
